import { createSlice } from "@reduxjs/toolkit";

const parseWorkspaceUserId = (url = "") => {
  const match = String(url).match(
    /(?:admin|service-provider\/customer)\/users\/(\d+)\/workspace-properties/,
  );
  return match ? Number(match[1]) : null;
};

const initialPropertyListsState = {
  propertyListData: {},
  userWorkspacePropertyListData: {},
  userWorkspacePropertyListUserId: null,
  userWorkspacePropertiesLoader: false,
  propertyListDataCount: 0,
  propertiesLoader: false,
  propertyRequestDocument: {},
  uploadedPropertyDocuments: {},
  replacedPropertyDocuments: {},
  uploadedPropertyGeneralDocuments: {},
  bindProfessionalDetails: {},
  bindProfessionalLoader: false,
  verifyAddressDetails: {},
  verifyAddressLoader: false,
  viewPropertyDetails: {},
  viewPropertyDetailsCache: {},
  viewPropertyDetailLoader: false,
  viewPropertyDetailLoadingKey: null,
  cancelSubscriptionLoader: false,
  canceledSubscriptionDetails: {},
  changeSubscriptionLoader: false,
  changeSubscriptionPlanList: {},
  changedSubscriptionDetails: {},
  cancelChangeSubscriptionRequestLoader: false,
  cancelChangeSubscriptionRequestDetails: {},
  refundRecordingFeesDetails: {},
  refundRecordingFeesLoader: false,
  resendPostcardDetails: {},
  resendPostcardLoader: false,
  updateMailingAddressDetails: {},
  updateZipCodeDetails: {},
  mailingAddressLoader: false,
  closingAdminDetails: {},
  closingAdminLoader: false,
  reRecordUCCDocData: {},
  emailHistory: {},
  emailHistoryLoader: false,
  emailHistoryDetail: {},
  emailHistoryDetailLoader: false,
  resendEmailHistoryDetails: {},
  resendEmailHistoryLoader: false,
  uccRecordingDetails: {},
  uccRecordingDetailsLoader: false,
  updatePropertyUccStatusDetails: {},
  updatePropertyUccStatusLoader: false,
};

const propertyLists = createSlice({
  name: "propertyLists",
  initialState: initialPropertyListsState,
  reducers: {
    getPropertyListData: (state, action) => {
      state.propertiesLoader = true;
      // Drop stale rows immediately so auto-open / UI cannot use a previous response.
      state.propertyListData = {};
      state.propertyListDataCount = 0;
      console.log("Get property list slice", action.payload);
    },
    clearPropertyListData: (state) => {
      state.propertyListData = {};
      state.propertyListDataCount = 0;
      state.propertiesLoader = false;
    },
    getUserWorkspacePropertyListData: (state, action) => {
      state.userWorkspacePropertiesLoader = true;
      const userId = parseWorkspaceUserId(action.payload?.url);
      if (userId) {
        if (state.userWorkspacePropertyListUserId !== userId) {
          state.userWorkspacePropertyListData = {};
        }
        state.userWorkspacePropertyListUserId = userId;
      }
      console.log("Get user workspace property list slice", action.payload);
    },
    fetchPropertyListData: (state, action) => {
      console.log("Fetching property list slice==>", action.payload?.data);
      state.propertyListData = action.payload;
      state.propertyListDataCount = action.payload?.count;
      state.propertiesLoader = false;
      //expire the cookie
      document.cookie =
        "iosValue =; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;";
    },
    fetchUserWorkspacePropertyListData: (state, action) => {
      state.userWorkspacePropertyListData = action.payload || {};
      state.userWorkspacePropertiesLoader = false;
    },
    clearPropertyRequestDocument(state, action) {
      state.bindProfessionalDetails = {};
      state.propertyRequestDocument = {};
      state.uploadedPropertyDocuments = {};
      state.replacedPropertyDocuments = {};
      state.reRecordUCCDocData = {};
      state.uccRecordingDetails = {};
      state.uccRecordingDetailsLoader = false;
    },
    getRequestPropertyDocument: (state, action) => {
      state.propertiesLoader = true;
      console.log("In request property document slice", action.payload);
    },
    fetchRequestPropertyDocument: (state, action) => {
      console.log("Fetching property document slice", action.payload?.data);
      state.propertyRequestDocument = action.payload;
      state.propertiesLoader = false;
    },
    getUploadedPropertyDocuments: (state, action) => {
      state.propertiesLoader = true;
      console.log("Getting uploaded docs", action.payload);
    },
    fetchUploadedPropertyDocuments: (state, action) => {
      console.log("Fetching uploaded documents", action.payload?.data);
      state.uploadedPropertyDocuments = action.payload;
      state.propertiesLoader = false;
    },
    getReplacedPropertyDocuments: (state, action) => {
      state.propertiesLoader = true;
      console.log("Getting replaced docs", action.payload);
    },
    fetchReplacedPropertyDocuments: (state, action) => {
      console.log("Fetching replaced documents", action.payload?.data);
      state.replacedPropertyDocuments = action.payload;
      state.propertiesLoader = false;
    },
    getUploadedPropertyGeneralDocuments: (state, action) => {
      state.propertiesLoader = true;
      console.log("Getting uploaded docs", action.payload);
    },
    fetchUploadedPropertyGeneralDocuments: (state, action) => {
      console.log("Fetching uploaded documents", action.payload?.data);
      state.uploadedPropertyGeneralDocuments = action.payload;
      state.propertiesLoader = false;
    },
    clearBindProfessionalDetails: (state, action) => {
      state.bindProfessionalDetails = {};
      state.bindProfessionalLoader = false;
    },
    bindProfessionalsToProperty: (state, action) => {
      state.bindProfessionalLoader = true;
    },
    setBindProfessionalDetails: (state, action) => {
      state.bindProfessionalDetails = action.payload;
      state.bindProfessionalLoader = false;
    },
    verifyAddress: (state, action) => {
      state.verifyAddressLoader = true;
    },
    setVerifyAddressDetails: (state, action) => {
      state.verifyAddressLoader = false;
      state.verifyAddressDetails = action.payload;
    },
    getAdminViewPropertyDetails: (state, action) => {
      const propertyId = action.payload?.data?.id;
      const category =
        action.payload?.data?.category ||
        action.payload?.data?.product_tier ||
        "complete_protection";
      const cacheKey = propertyId ? `${propertyId}:${category}` : null;
      const forceRefresh = Boolean(action.payload?.data?.refresh);

      if (
        cacheKey &&
        !forceRefresh &&
        state.viewPropertyDetailsCache[cacheKey]?.success
      ) {
        return;
      }

      state.viewPropertyDetailLoader = true;
      state.viewPropertyDetailLoadingKey = cacheKey;
    },
    setAdminViewPropertyDetails: (state, action) => {
      state.viewPropertyDetails = action.payload;
      state.viewPropertyDetailLoader = false;
      state.viewPropertyDetailLoadingKey = null;

      const data = action.payload?.data;
      if (action.payload?.success && data?.id) {
        const category =
          data.product_tier || data.category || "complete_protection";
        const cacheKey = `${data.id}:${category}`;
        state.viewPropertyDetailsCache[cacheKey] = action.payload;
      }
    },
    clearViewPropertyDetailsCache: (state, action) => {
      const propertyId = action.payload?.propertyId;

      if (propertyId) {
        const prefix = `${propertyId}:`;
        Object.keys(state.viewPropertyDetailsCache).forEach((key) => {
          if (key.startsWith(prefix)) {
            delete state.viewPropertyDetailsCache[key];
          }
        });

        if (
          state.viewPropertyDetails?.success &&
          state.viewPropertyDetails?.data?.id === propertyId
        ) {
          state.viewPropertyDetails = {};
        }

        return;
      }

      state.viewPropertyDetailsCache = {};
      state.viewPropertyDetails = {};
    },
    cancelSubscription: (state, action) => {
      state.cancelSubscriptionLoader = true;
    },
    setCanceledSubscription: (state, action) => {
      state.canceledSubscriptionDetails = action.payload;
      state.cancelSubscriptionLoader = false;
    },
    getChangeSubscriptionPlanList(state, action) {
      state.changeSubscriptionLoader = true;
    },
    setChangeSubscriptionPlanList(state, action) {
      state.changeSubscriptionPlanList = action?.payload;
      state.changeSubscriptionLoader = false;
    },
    changeSubscriptionPlan(state, action) {
      state.changeSubscriptionLoader = true;
    },
    setChangedSubscriptionPlan(state, action) {
      state.changedSubscriptionDetails = action?.payload;
      state.changeSubscriptionLoader = false;
    },
    cancelChangeSubscriptionRequest(state, action) {
      state.cancelChangeSubscriptionRequestLoader = true;
    },
    setCancelChangeSubscriptionRequest(state, action) {
      state.cancelChangeSubscriptionRequestDetails = action?.payload;
      state.cancelChangeSubscriptionRequestLoader = false;
    },
    sendRefundRecordingFees: (state, action) => {
      state.refundRecordingFeesLoader = true;
    },
    setRefundRecordingFeesDetails: (state, action) => {
      state.refundRecordingFeesDetails = action?.payload;
      state.refundRecordingFeesLoader = false;
    },
    resendPostcard(state, action) {
      state.resendPostcardLoader = true;
    },
    setResendPostcardDetails(state, action) {
      state.resendPostcardDetails = action?.payload;
      state.resendPostcardLoader = false;
    },
    getUpdateMailingAddress(state, action) {
      state.mailingAddressLoader = true;
    },
    fetchUpdateMailingAddressDetails(state, action) {
      state.updateMailingAddressDetails = action?.payload;
      state.mailingAddressLoader = false;
    },
    getUpdateZipCode(state, action) {
      state.mailingAddressLoader = true;
    },
    fetchUpdateZipCodeDetails(state, action) {
      state.updateZipCodeDetails = action?.payload;
      state.mailingAddressLoader = false;
    },
    getClosingAdminDetails(state, action) {
      state.closingAdminLoader = true;
    },
    fetchClosingAdminDetails(state, action) {
      state.closingAdminDetails = action?.payload;
      state.closingAdminLoader = false;
    },
    getReRecordUCCDocData: (state, action) => {
      state.closingAdminLoader = true;
      console.log("In request property document slice", action.payload);
    },
    fetchReRecordUCCDocData: (state, action) => {
      console.log("Fetching property document slice", action.payload?.data);
      state.reRecordUCCDocData = action.payload;
      state.closingAdminLoader = false;
    },
    fetchUserEmailHistory: (state, action) => {
      state.emailHistoryLoader = true;
    },
    setUserEmailHistory: (state, action) => {
      state.emailHistory = action.payload;
      state.emailHistoryLoader = false;
    },
    clearUserEmailHistory: (state) => {
      state.emailHistory = {};
      state.emailHistoryLoader = false;
      state.emailHistoryDetail = {};
      state.emailHistoryDetailLoader = false;
      state.resendEmailHistoryDetails = {};
      state.resendEmailHistoryLoader = false;
    },
    fetchEmailHistoryDetail: (state) => {
      state.emailHistoryDetail = {};
      state.emailHistoryDetailLoader = true;
    },
    setEmailHistoryDetail: (state, action) => {
      state.emailHistoryDetail = action.payload;
      state.emailHistoryDetailLoader = false;
    },
    clearEmailHistoryDetail: (state) => {
      state.emailHistoryDetail = {};
      state.emailHistoryDetailLoader = false;
    },
    resendEmailHistory: (state) => {
      state.resendEmailHistoryDetails = {};
      state.resendEmailHistoryLoader = true;
    },
    setResendEmailHistory: (state, action) => {
      state.resendEmailHistoryDetails = action.payload;
      state.resendEmailHistoryLoader = false;
    },
    clearResendEmailHistory: (state) => {
      state.resendEmailHistoryDetails = {};
      state.resendEmailHistoryLoader = false;
    },
    updateUccRecordingDetails: (state) => {
      state.uccRecordingDetailsLoader = true;
    },
    setUccRecordingDetails: (state, action) => {
      state.uccRecordingDetails = action.payload;
      state.uccRecordingDetailsLoader = false;
    },
    clearUccRecordingDetails: (state) => {
      state.uccRecordingDetails = {};
      state.uccRecordingDetailsLoader = false;
    },
    updatePropertyUccStatus: (state) => {
      state.updatePropertyUccStatusLoader = true;
    },
    setUpdatePropertyUccStatus: (state, action) => {
      state.updatePropertyUccStatusDetails = action.payload;
      state.updatePropertyUccStatusLoader = false;
    },
    clearUpdatePropertyUccStatus: (state) => {
      state.updatePropertyUccStatusDetails = {};
      state.updatePropertyUccStatusLoader = false;
    },
  },
});
export const propertyListAction = propertyLists.actions;
export default propertyLists.reducer;
